import type { SVGProps } from 'react';

/**
 * A tiny inline icon set (24px grid, stroke-based, inherits `currentColor`)
 * so the page ships no icon-font or icon-library payload.
 */
const paths = {
  compass: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M14.9 9.1l-1.7 4.1-4.1 1.7 1.7-4.1z" />
    </>
  ),
  layers: (
    <>
      <path d="M12 3l8 4.5-8 4.5-8-4.5z" />
      <path d="M4 12.5l8 4.5 8-4.5" />
      <path d="M4 16.8l8 4.5 8-4.5" />
    </>
  ),
  radar: (
    <>
      <path d="M12 12V3a9 9 0 108.5 6" />
      <circle cx="12" cy="12" r="3.4" />
      <path d="M12 12l6-6" />
    </>
  ),
  arrowRight: <path d="M4 12h15m-6-6l6 6-6 6" />,
  arrowUpRight: <path d="M7 17L17 7m-8 0h8v8" />,
  check: <path d="M4.5 12.5l4.5 4.5L19.5 6.5" />,
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2m0 16v2M2 12h2m16 0h2M4.9 4.9l1.5 1.5m11.2 11.2l1.5 1.5M19.1 4.9l-1.5 1.5M6.4 17.6l-1.5 1.5" />
    </>
  ),
  moon: <path d="M20 14.5A8.5 8.5 0 019.5 4a8.5 8.5 0 1010.5 10.5z" />,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path d="M3.5 7.5l8.5 6 8.5-6" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21s7-6.2 7-11a7 7 0 10-14 0c0 4.8 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </>
  ),
  spark: <path d="M12 3l2.2 6.3L20.5 12l-6.3 2.2L12 21l-2.2-6.8L3.5 12l6.3-2.2z" />,
} as const;

export type IconName = keyof typeof paths;

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name' | 'children'> {
  name: IconName;
  /** Rendered size in px for both axes. */
  size?: number;
}

export function Icon({ name, size = 20, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {paths[name]}
    </svg>
  );
}
